import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Button } from "../../../design/button";
import { Input } from "../../../design/input";
import { Select } from "../../../design/select";
import {
  Fieldset,
  Field,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { ParentingPlan } from "../../../models/ParentingPlan";
import { useParentingPlan } from "../../../contexts/ParentingPlanContext";
import ParentingPlanStatusBadge from "../../Badges/ParentingPlanStatusBadge";
import { documentStatusOptions } from "../../../constants/documentStatusOptions";
import { ParentingPlanStatus } from "../../../models/ParentingPlanStatus";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  parentingPlan?: ParentingPlan;
  onSaved?: () => void;
}

const ParentingPlanDialog: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  parentingPlan,
  onSaved,
}) => {
  const { createParentingPlan, updateParentingPlan } = useParentingPlan();
  const [name, setName] = useState("");
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<ParentingPlanStatus>("draft");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen && parentingPlan) {
      setName(parentingPlan.name);
      setDescription(parentingPlan.description);
      setStatus(parentingPlan.status);
      setEffectiveDate(
        parentingPlan.effectiveDate
          ? new Date(parentingPlan.effectiveDate).toISOString().split("T")[0]
          : "",
      );
      setExpirationDate(
        parentingPlan.expirationDate
          ? new Date(parentingPlan.expirationDate).toISOString().split("T")[0]
          : "",
      );
    } else if (isOpen) {
      setName("");
      setDescription("");
      setStatus("draft");
      setEffectiveDate("");
      setExpirationDate("");
    }
  }, [isOpen, parentingPlan]);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);

    try {
      if (parentingPlan) {
        await updateParentingPlan(parentingPlan._id, {
          name,
          description,
          status,
          effectiveDate: effectiveDate ? new Date(effectiveDate) : undefined,
          expirationDate: expirationDate ? new Date(expirationDate) : undefined,
        });
      } else {
        await createParentingPlan({
          name,
          description,
          effectiveDate: effectiveDate ? new Date(effectiveDate) : undefined,
          expirationDate: expirationDate ? new Date(expirationDate) : undefined,
          status,
          generationMethod: "manual",
        });
      }
      onSaved?.();
      setIsOpen(false);
    } catch (err: any) {
      console.error("Failed to save employment info", err);
      setErrors({ employer: "Something went wrong. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogTitle>
        {parentingPlan ? "Edit Parenting Plan" : "Create Parenting Plan"}
      </DialogTitle>
      <DialogBody>
        <Fieldset disabled={isSaving}>
          <FieldGroup>
            <Field>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter name"
                required
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>

            <Field>
              <Label htmlFor="type">Status</Label>
              <Select
                id="type"
                value={parentingPlan?.status || ""}
                onChange={(e) =>
                  setStatus(e.target.value as ParentingPlanStatus)
                }
              >
                <option value="" disabled>
                  Select Status
                </option>
                {documentStatusOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </Field>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label htmlFor="effectiveDate">Effective Date</Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="expirationDate">Expiration Date</Label>
                <Input
                  id="expirationDate"
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                />
              </Field>
            </div>
          </FieldGroup>
        </Fieldset>
      </DialogBody>

      <DialogActions>
        <Button plain onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <Button disabled={isSaving} onClick={handleSave}>
          {isSaving ? "Saving..." : parentingPlan ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParentingPlanDialog;
